import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Icon, Result, Space, Spin, Tag, Text, Title, useToast } from "@app/ui";
import { acceptInvite, previewInvite } from "../api/dpp.js";
import { useAuth } from "../auth/AuthContext.js";
import { AuthShell } from "../auth/AuthShell.js";
import { useOrgs } from "../orgs/OrgContext.js";
import { useT } from "../i18n.js";
import { useErrorText } from "../errors/index.js";
import type { InvitePreview } from "../types.js";

/**
 * Invitation landing page. Public: shows the invite preview (org + role) before
 * sign-in. Signed in with the invited email → accept in place; signed in with a
 * different account → one-click switch; signed out → stash the token and go
 * sign in / register (the login page resumes here afterwards).
 */
export function AcceptInvitePage() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const errorText = useErrorText();
  const { user, logout } = useAuth();
  const { refreshOrgs, selectOrg } = useOrgs();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("invites.acceptMissingToken"));
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        setPreview(await previewInvite(token));
      } catch (e) {
        setError(errorText(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const emailMatches = !!user && !!preview && user.email.toLowerCase() === preview.email.toLowerCase();

  const goSignIn = () => {
    if (token) sessionStorage.setItem("pendingInviteToken", token);
    navigate("/login");
  };

  // Wrong account signed in: end that session and resume the invite at the login page.
  const switchAccount = async () => {
    setSwitching(true);
    try {
      if (token) sessionStorage.setItem("pendingInviteToken", token);
      await logout();
      navigate("/login");
    } finally {
      setSwitching(false);
    }
  };

  const accept = async () => {
    if (!token) return;
    setAccepting(true);
    try {
      const res = await acceptInvite(token);
      toast.success(t("invites.acceptSuccess"));
      await refreshOrgs(res.orgId);
      selectOrg(res.orgId);
      navigate("/dashboard");
    } catch (e) {
      setError(errorText(e));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <AuthShell>
        <div className="auth-wait">
          <Spin />
          <Text secondary>{t("invites.loading")}</Text>
        </div>
      </AuthShell>
    );
  }

  if (error && !preview) {
    return (
      <AuthShell>
        <Result
          status="error"
          title={t("invites.acceptErrorTitle")}
          subTitle={error}
          extra={
            <Button variant="primary" onClick={() => navigate(user ? "/" : "/login")}>
              {user ? t("verify.goDashboard") : t("verify.goLogin")}
            </Button>
          }
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Space direction="vertical" size="md" style={{ width: "100%" }}>
        <Title level={3}>{t("invites.acceptTitle")}</Title>
        <Text>
          {t("invites.acceptBody")} <strong>{preview?.orgName}</strong>{" "}
          {preview && <Tag>{t(`role.${preview.role}`)}</Tag>}
        </Text>
        <div className="auth-invite-email">
          <Icon name="mail" size={14} />
          <Text secondary>{preview?.email}</Text>
        </div>
        {error && <Alert type="error">{error}</Alert>}

        {!user ? (
          <>
            <Alert type="info">
              {t("invites.acceptSignInPrompt")} <strong>{preview?.email}</strong>
            </Alert>
            <Button variant="primary" block onClick={goSignIn}>
              {t("invites.acceptSignIn")}
            </Button>
          </>
        ) : emailMatches ? (
          <Button variant="primary" block loading={accepting} onClick={() => void accept()}>
            {t("invites.acceptButton")}
          </Button>
        ) : (
          <>
            <Alert type="warning">
              {t("invites.acceptWrongAccount")} <strong>{preview?.email}</strong>
            </Alert>
            <Button variant="primary" block loading={switching} onClick={() => void switchAccount()}>
              {t("invites.acceptSwitch")}
            </Button>
          </>
        )}
      </Space>
    </AuthShell>
  );
}
